import { getAuthToken } from './auth/tokenStorage';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { API_URL } from '../config';

import { createLogger } from '../utils/logger';

const logger = createLogger('specialtyPhysician');
export interface SpecialtyPhysicianInfo {
  id: string;
  taskId: number;
  fullName: string;
  phoneNumber: string;
  email: string;
  specialty: string;
  officeAddress: string;
  state: string;
  notes: string;
}

export interface CreateSpecialtyPhysicianInfoData {
  taskId: number;
  fullName?: string;
  phoneNumber?: string;
  email?: string;
  specialty?: string;
  officeAddress?: string;
  state?: string;
  notes?: string;
}

export interface UpdateSpecialtyPhysicianInfoData extends CreateSpecialtyPhysicianInfoData {}

class SpecialtyPhysicianInfoService {
  private async getAuthToken(): Promise<string | null> {
    return await getAuthToken();
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const token = await this.getAuthToken();
    const fullUrl = `${API_URL}${endpoint}`;
    
    logger.debug(`[SpecialtyPhysicianService] Making ${options.method || 'GET'} request to:`, fullUrl);
    
    const response = await fetch(fullUrl, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
    });

    return response;
  }

  async getAllSpecialtyPhysicianInfo(targetUserId?: string): Promise<SpecialtyPhysicianInfo[]> {
    const endpoint = targetUserId
      ? `/api/specialty-physician-info?targetUserId=${targetUserId}`
      : '/api/specialty-physician-info';
    const response = await this.makeRequest(endpoint);

    if (!response.ok) {
      throw new Error(`Failed to fetch specialty physician info: ${response.status}`);
    }

    const result = await response.json();
    return result.data || result;
  }

  async getSpecialtyPhysicianInfo(taskId: number): Promise<SpecialtyPhysicianInfo[]> {
    const response = await this.makeRequest(`/api/specialty-physician-info?taskId=${taskId}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch specialty physician info: ${response.status}`);
    }

    const result = await response.json();
    return result.data || result;
  }

  async createSpecialtyPhysicianInfo(data: CreateSpecialtyPhysicianInfoData): Promise<{ id: string }> {
    const response = await this.makeRequest('/api/specialty-physician-info', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to create specialty physician info: ${response.status}`);
    }

    const result = await response.json();
    return result.data;
  }

  async updateSpecialtyPhysicianInfo(id: string, data: UpdateSpecialtyPhysicianInfoData): Promise<SpecialtyPhysicianInfo> {
    const response = await this.makeRequest(`/api/specialty-physician-info/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to update specialty physician info: ${response.status}`);
    }

    const result = await response.json();
    return result.data;
  }

  async deleteSpecialtyPhysicianInfo(id: string): Promise<void> {
    const response = await this.makeRequest(`/api/specialty-physician-info/${id}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error(`Failed to delete specialty physician info: ${response.status}`);
    }
  }


  async lockSpecialtyPhysicianInfo(id: string): Promise<void> {
    const response = await this.makeRequest(`/api/specialty-physician-info/${id}/lock`, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to lock specialty physician info: ${response.status}`);
    }
  }

  async unlockSpecialtyPhysicianInfo(id: string, granteeEmails: string[]): Promise<void> {
    const response = await this.makeRequest(`/api/specialty-physician-info/${id}/unlock`, {
      method: 'POST',
      body: JSON.stringify({ granteeEmails }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to unlock specialty physician info: ${response.status}`);
    }
  }

  async getSpecialtyPhysicianAccessGrants(id: string): Promise<any[]> {
    const response = await this.makeRequest(`/api/specialty-physician-info/${id}/access-grants`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to get access grants: ${response.status}`);
    }

    const result = await response.json();
    return result.data || [];
  }

  async markAsCompleted(taskId: number): Promise<boolean> {
    const response = await this.makeRequest(`/api/specialty-physician-info/complete/${taskId}`, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to mark specialty physician info as completed: ${response.status}`);
    }

    const result = await response.json();
    return result.data || result;
  }
}

export const specialtyPhysicianInfoService = new SpecialtyPhysicianInfoService();